import React from 'react'
import Box from '@mui/material/Box'
// import { EditorContent } from "@tiptap/react";
import { useMemo } from 'react'
import type { CSSObject } from 'tss-react'
import { makeStyles } from 'tss-react/mui'
import { useCurrentEditor } from '@tiptap/react'
import { getEditorStyles, getUtilityClasses } from './styles'
// import ScrollBar from 'react-perfect-scrollbar'

export type RichTextContentClasses = ReturnType<typeof useStyles>['classes']

export type RichTextContentProps = {
  /** Optional additional className to provide to the root element. */
  className?: string
  /** Override or extend existing styles. */
  children: React.ReactNode
  classes?: Partial<RichTextContentClasses>
  removePadding?: boolean
}

const richTextContentClasses: RichTextContentClasses = getUtilityClasses(
  DocumentContent.name,
  ['root', 'readonly', 'editable']
)

const useStyles = makeStyles({ name: { DocumentContent } })((theme) => {
  return {
    root: {
      // We add `as CSSObject` to get around typing issues with our editor
      // styles function. For future reference, this old issue and its solution
      // are related, though not quite right
      // https://github.com/garronej/tss-react/issues/2
      // https://github.com/garronej/tss-react/commit/9dc3f6f9f70b6df0bd83cd5689c3313467fb4f06
      '.ProseMirror': {
        ...getEditorStyles(theme),
      } as CSSObject,
    },

    // Styles applied when the editor is in read-only mode (editable=false)
    readonly: {},

    // Styles applied when the editor is editable (editable=true)
    editable: {},
  }
})

/**
 * A component for rendering a MUI-styled version of Tiptap rich text editor
 * content.
 *
 * Must be a child of the RichTextEditorProvider so that the `editor` context is
 * available.
 */
export function DocumentContent({
  className,
  children,
  classes: overrideClasses = {},
  removePadding = false,
}: RichTextContentProps) {
  const { classes, cx } = useStyles(undefined, {
    props: { classes: overrideClasses },
  })
  const { editor } = useCurrentEditor()
  const editorClasses = useMemo(
    () =>
      cx(
        richTextContentClasses.root,
        className,
        classes.root,
        editor?.isEditable
          ? [richTextContentClasses.editable, classes.editable]
          : [richTextContentClasses.readonly, classes.readonly]
      ),
    [className, classes, cx, editor?.isEditable]
  )

  return (
    <Box
      style={{
        height: '100%',
        overflowY: 'auto',
        paddingRight: removePadding ? 'none' : '15px',
      }}
    >
      <Box
        className={editorClasses}
        sx={{
          pl: removePadding ? 0 : 11,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          flexGrow: 1,
          // overflow: 'hidden',
          '& *': {
            wordBreak: 'break-all !important',
            whiteSpace: 'pre-wrap !important',
          },
        }}
      >
        {children}
      </Box>
    </Box>
  )
}
